// src/utils/supabaseClient.ts
/**
 * HOMEQUEST TECH - SUPABASE CLIENT
 * FINAL WORKING VERSION
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';

// ============================================
// GET FROM ENVIRONMENT VARIABLES
// ============================================
// Clean the values to remove any whitespace or newline characters
const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || 'https://fbwmkkskdrvaipmkddwm.supabase.co').trim();
const supabaseAnonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZid21ra3NrZHJ2YWlwbWtkZHdtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTE2ODI4MTcsImV4cCI6MjA2NzI1ODgxN30.-rBrI8a56Pc-5ROhiZaGtK6QwH1qrZOt7Osmj-lqeJc').trim();

// Debug log to verify keys are loading
console.log('🔐 Supabase Client Initialization:');
console.log('- URL:', supabaseUrl);
console.log('- Key loaded:', supabaseAnonKey ? '✅ Yes' : '❌ No');
console.log('- Key prefix:', supabaseAnonKey.substring(0, 30) + '...');
console.log('- From env:', import.meta.env.VITE_SUPABASE_ANON_KEY ? '✅ Yes' : '❌ No (using fallback)');

// Create the client with improved configuration for better performance
export const supabase: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    storageKey: 'sb-fbwmkkskdrvaipmkddwm-auth-token',
    flowType: 'pkce', // More secure auth flow
    debug: false, // Set to true if you need auth debugging
  },
  global: {
    headers: {
      'x-client-info': 'homequest-tech@1.0.0'
    },
    fetch: (url, options = {}) => {
      // No timeout - let requests complete naturally
      // The 60s timeout was causing issues with slow auth checks
      return fetch(url, {
        ...options,
        // Add keepalive for better connection persistence
        keepalive: true
      });
    }
  },
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 2 // Reduce realtime load
    }
  }
});

// ============================================
// TEST CONNECTION
// ============================================
export const testSupabaseConnection = async (): Promise<boolean> => {
  try {
    console.log('🔍 Testing Supabase connection...');
    
    // Test 1: Try to fetch from a public table
    const { error: dbError } = await supabase
      .from('projects')
      .select('count')
      .limit(1);
    
    if (dbError && dbError.code !== 'PGRST116') {
      console.error('❌ Database error:', dbError);
      return false;
    }
    
    console.log('✅ Supabase database connected!');
    
    // Test 2: Test Edge Function
    const response = await fetch(
      `${supabaseUrl}/functions/v1/ai-vision`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'apikey': supabaseAnonKey
        },
        body: JSON.stringify({ test: true })
      }
    );
    
    if (response.ok) {
      const data = await response.json();
      console.log('✅ Edge Function working!');
      console.log('- Has OpenAI Key:', data.hasOpenAIKey ? '✅' : '❌');
      
      if (!data.hasOpenAIKey) {
        console.warn('⚠️ OpenAI key not set in Edge Function');
        console.log('Run: supabase secrets set OPENAI_API_KEY=your-key');
      }
    } else {
      console.error('❌ Edge Function error:', response.status);
    }
    
    return true;
    
  } catch (err) {
    console.error('❌ Connection test failed:', err);
    return false;
  }
};

// ============================================
// CONFIGURATION CHECK
// ============================================
export const isSupabaseConfigured = (): boolean => {
  return !!(supabaseUrl && supabaseAnonKey);
};

// ============================================
// AUTH UTILITIES
// ============================================
export const getCurrentUser = async () => {
  if (!supabase) return null;
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

export const getSession = async () => {
  if (!supabase) return { data: { session: null }, error: 'Supabase not configured' };
  const { data, error } = await supabase.auth.getSession();
  return { data: { session: data?.session || null }, error };
};

// ============================================
// DATABASE UTILITIES
// ============================================
export const from = (table: string) => {
  if (!supabase) throw new Error('Supabase client not available');
  return supabase.from(table);
};

// ============================================
// AUTH OBJECT
// ============================================
export const auth = () => {
  if (!supabase) throw new Error('Supabase client not available');
  return supabase.auth;
};

// ============================================
// STORAGE OBJECT
// ============================================
export const storage = () => {
  if (!supabase) throw new Error('Supabase client not available');
  return supabase.storage;
};

// ============================================
// FUNCTIONS (for Edge Functions)
// ============================================
export const functions = () => {
  if (!supabase) throw new Error('Supabase client not available');
  return supabase.functions;
};

// ============================================
// AUTO-TEST ON LOAD (Development only)
// ============================================
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  console.log('🏗️ HomeQuest Tech Starting...');
  
  // Run connection test after a delay to ensure everything is loaded
  setTimeout(() => {
    testSupabaseConnection().then(success => {
      if (success) {
        console.log('✅ All systems operational!');
        console.log('📊 OpenAI Key:', import.meta.env.VITE_OPENAI_API_KEY ? '✅ Set' : '❌ Missing');
      } else {
        console.error('⚠️ Some services not working - check errors above');
      }
    });
  }, 1000);
}

// ============================================
// DEFAULT EXPORT
// ============================================
export default supabase;